import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiPath from "../../constants/apiPath";

const DAYS_ORDER = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];

const DAY_LABELS = {
  SUNDAY: "Sunday",
  MONDAY: "Monday",
  TUESDAY: "Tuesday",
  WEDNESDAY: "Wednesday",
  THURSDAY: "Thursday",
  FRIDAY: "Friday",
  SATURDAY: "Saturday",
};

const BLOCK_COLORS = {
  A: "#004aad",
  B: "#6366f1",
  C: "#8b5cf6",
  D: "#ec4899",
  E: "#f59e0b",
  F: "#10b981",
};

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const day = date.getDate();
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return `${day} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

function getNext7Days(timezone) {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const dateStr = d.toLocaleDateString("en-CA", { timeZone: timezone });
    const dayName = d
      .toLocaleDateString("en-US", { timeZone: timezone, weekday: "long" })
      .toUpperCase();
    days.push({ dateStr, dayName });
  }
  return days;
}

function ExerciseRow({ exercise }) {
  if (exercise.isHeader) {
    return (
      <div
        style={{
          background: "rgba(252, 220, 8, 0.1)",
          borderLeft: "3px solid #fcdc08",
          padding: "6px 12px",
          marginTop: 8,
          marginBottom: 4,
          borderRadius: 4,
        }}
      >
        <span style={{ color: "#fcdc08", fontWeight: 700, fontSize: 13 }}>
          {exercise.name}
        </span>
      </div>
    );
  }

  const showSets = exercise.showSets !== false;
  const showReps = exercise.showReps !== false;
  const showTempo = exercise.showTempo !== false;
  const hasColumns = showSets || showReps || showTempo;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 12px",
        borderBottom: "1px solid rgba(255,255,255,0.04)",
      }}
    >
      <span style={{ color: "#e2e8f0", fontSize: 14, flex: 1 }}>
        {exercise.name}
      </span>
      {hasColumns && (
        <div style={{ display: "flex", gap: 16, flexShrink: 0 }}>
          {showSets && exercise.sets != null && (
            <span style={{ color: "#94a3b8", fontSize: 13, minWidth: 50, textAlign: "center" }}>
              <span style={{ color: "#64748b", fontSize: 11 }}>Sets </span>
              {exercise.sets}
            </span>
          )}
          {showReps && exercise.reps != null && (
            <span style={{ color: "#94a3b8", fontSize: 13, minWidth: 50, textAlign: "center" }}>
              <span style={{ color: "#64748b", fontSize: 11 }}>Reps </span>
              {exercise.reps}
            </span>
          )}
          {showTempo && exercise.tempo != null && (
            <span style={{ color: "#94a3b8", fontSize: 13, minWidth: 50, textAlign: "center" }}>
              <span style={{ color: "#64748b", fontSize: 11 }}>Tempo </span>
              {exercise.tempo}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function BlockCard({ block }) {
  const letter = block.key ? block.key.replace('_BLOCK', '') : "A";
  const color = BLOCK_COLORS[letter] || BLOCK_COLORS.A;
  const title = block.blockName || block.title || "";
  const duration = block.duration || "";
  const variant = block.activeVariant || "";
  const exercises = block.tasks || [];
  const notes = block.cooldown || "";
  const timeSegments = block.timeSegments || [];
  const showSets = block.showSets !== false;
  const showReps = block.showReps !== false;
  const showTempo = block.showTempo !== false;

  return (
    <div
      style={{
        background: "#1a1a2e",
        borderRadius: 10,
        border: "1px solid rgba(255,255,255,0.06)",
        marginBottom: 10,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <span
          style={{
            background: color,
            color: "#fff",
            fontWeight: 800,
            fontSize: 13,
            width: 28,
            height: 28,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {letter}
        </span>
        <div style={{ flex: 1 }}>
          <span style={{ color: "#f1f5f9", fontWeight: 600, fontSize: 14 }}>
            {title}
          </span>
          {duration && (
            <span style={{ color: "#64748b", fontSize: 12, marginLeft: 8 }}>
              {duration}
            </span>
          )}
        </div>
        {variant && (
          <span
            style={{
              background: "rgba(0, 74, 173, 0.2)",
              color: "#60a5fa",
              fontSize: 11,
              padding: "2px 8px",
              borderRadius: 4,
              fontWeight: 600,
            }}
          >
            {variant}
          </span>
        )}
      </div>

      {exercises.length > 0 && (() => {
        // Skip index 0 so a section header at the top (often marked isSplit) doesn't produce an empty left panel.
        const splitIdx = exercises.findIndex((ex, i) => i >= 1 && ex.isSplit);
        if (splitIdx >= 1) {
          const leftExercises = exercises.slice(0, splitIdx);
          const rightExercises = exercises.slice(splitIdx);
          return (
            <div style={{ display: "flex", gap: 0, width: "100%" }}>
              <div style={{ flex: 1 }}>
                {leftExercises.map((ex, i) => (
                  <ExerciseRow key={i} exercise={{ ...ex, showSets, showReps, showTempo }} />
                ))}
              </div>
              <div style={{ width: 2, background: "rgba(252,220,8,0.3)", alignSelf: "stretch" }} />
              <div style={{ flex: 1 }}>
                {rightExercises.map((ex, i) => (
                  <ExerciseRow key={`r${i}`} exercise={{ ...ex, showSets, showReps, showTempo }} />
                ))}
              </div>
            </div>
          );
        }
        return (
          <div>
            {exercises.map((ex, i) => (
              <ExerciseRow key={i} exercise={{ ...ex, showSets, showReps, showTempo }} />
            ))}
          </div>
        );
      })()}

      {timeSegments.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            padding: "8px 14px",
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          {timeSegments.map((seg, i) => {
            const isOn =
              seg.type === "ON" ||
              seg.type === "on" ||
              seg.type === "WORK" ||
              seg.type === "work";
            return (
              <span
                key={i}
                style={{
                  background: isOn
                    ? "rgba(0, 74, 173, 0.25)"
                    : "rgba(245, 158, 11, 0.2)",
                  color: isOn ? "#60a5fa" : "#fbbf24",
                  fontSize: 11,
                  padding: "3px 8px",
                  borderRadius: 4,
                  fontWeight: 600,
                }}
              >
                {seg.label || seg.type} {seg.duration || seg.time || ""}
              </span>
            );
          })}
        </div>
      )}

      {notes && (
        <div style={{
          padding: "10px 14px",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          background: "rgba(252,220,8,0.04)",
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <span style={{ color: "#fcdc08", fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", whiteSpace: "nowrap", marginTop: 2 }}>NOTES</span>
            <p style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.5, margin: 0, whiteSpace: "pre-wrap" }}>{notes}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function DayCard({ dayName, dateStr, blocks, isToday, isPast }) {
  const opacity = isPast ? 0.45 : 1;

  return (
    <div
      style={{
        background: isToday
          ? "linear-gradient(135deg, rgba(0,74,173,0.12), rgba(252,220,8,0.06))"
          : "#12121f",
        borderRadius: 14,
        border: isToday
          ? "1.5px solid rgba(0, 74, 173, 0.5)"
          : "1px solid rgba(255,255,255,0.06)",
        padding: 16,
        opacity,
        transition: "opacity 0.2s",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div>
          <span
            style={{
              color: isToday ? "#fcdc08" : "#e2e8f0",
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            {DAY_LABELS[dayName] || dayName}
          </span>
          <span style={{ color: "#64748b", fontSize: 13, marginLeft: 8 }}>
            {formatDate(dateStr)}
          </span>
        </div>
        {isToday && (
          <span
            style={{
              background: "#fcdc08",
              color: "#0d0d1a",
              fontSize: 11,
              fontWeight: 700,
              padding: "2px 10px",
              borderRadius: 10,
            }}
          >
            TODAY
          </span>
        )}
      </div>

      {blocks && blocks.length > 0 ? (
        blocks.map((block, i) => <BlockCard key={i} block={block} />)
      ) : (
        <div
          style={{
            color: "#475569",
            fontSize: 13,
            textAlign: "center",
            padding: "20px 0",
          }}
        >
          Rest day
        </div>
      )}
    </div>
  );
}

export default function CoachView() {
  const { gymCode } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [retryCount, setRetryCount] = useState(0);

  const goBack = () => navigate("/");

  const retry = () => {
    setLoading(true);
    setError(null);
    setData(null);
    setRetryCount(c => c + 1);
  };

  useEffect(() => {
    if (!gymCode) {
      setError("No gym code provided");
      setLoading(false);
      return;
    }

    // Safety timeout — if still loading after 12s, show error
    const timeout = setTimeout(() => {
      setError("Connection timed out. Check your internet connection and try again.");
      setLoading(false);
    }, 12000);

    fetch(`${apiPath.baseURL}gym_owner/coach/${gymCode}?pin=${gymCode}`)
      .then((res) => {
        if (!res.ok) throw new Error("Invalid PIN or server error (" + res.status + ")");
        return res.json();
      })
      .then((json) => {
        clearTimeout(timeout);
        if (json.error) throw new Error(json.error);
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        clearTimeout(timeout);
        setError(err.message || "Unable to connect");
        setLoading(false);
      });

    return () => clearTimeout(timeout);
  }, [gymCode, retryCount]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingWrap}>
          <div style={styles.spinner} />
          <p style={{ color: "#94a3b8", marginTop: 16, fontSize: 14 }}>
            Loading programme...
          </p>
          <button
            onClick={goBack}
            style={{ marginTop: 28, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#64748b", padding: "8px 20px", borderRadius: 8, cursor: "pointer", fontSize: 13 }}
          >
            ← Back to login
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorWrap}>
          <div style={{ fontSize: 40, marginBottom: 12, color: "#f87171" }}>!</div>
          <p style={{ color: "#f87171", fontSize: 16, fontWeight: 600 }}>
            Unable to load programme
          </p>
          <p style={{ color: "#64748b", fontSize: 13, marginTop: 6, textAlign: "center", maxWidth: 300, lineHeight: 1.6 }}>
            {error}
          </p>
          <div style={{ display: "flex", gap: 12, marginTop: 28 }}>
            <button
              onClick={retry}
              style={{ background: "#004aad", border: "none", color: "#fff", padding: "10px 24px", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 }}
            >
              ↺ Retry
            </button>
            <button
              onClick={goBack}
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#94a3b8", padding: "10px 24px", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 }}
            >
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const timezone = data?.timezone || "Australia/Sydney";
  const fullWeek = data?.full_week || data?.fullWeek || {};
  const datesData = fullWeek.dates || {};
  const gymName = data?.gym || data?.gym_name || "";
  const gymLogo = data?.logo || data?.gym_logo || "";

  const getWeekDays = (offset) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() + (offset * 7) + i - d.getDay() + 1); // Start from Monday of that week
      const dateStr = d.toLocaleDateString("en-CA", { timeZone: timezone });
      const dayName = d.toLocaleDateString("en-US", { weekday: "long", timeZone: timezone }).toUpperCase();
      const shortDay = d.toLocaleDateString("en-US", { weekday: "short", timeZone: timezone });
      const dayNum = d.toLocaleDateString("en-US", { day: "numeric", timeZone: timezone });
      const monthShort = d.toLocaleDateString("en-US", { month: "short", timeZone: timezone });
      days.push({ dateStr, dayName, shortDay, dayNum, monthShort });
    }
    return days;
  };

  const weekDays = getWeekDays(weekOffset);
  const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: timezone });

  const dayCards = weekDays.map(({ dateStr, dayName, shortDay, dayNum, monthShort }) => {
    const dateEntry = datesData[dateStr];
    const dayEntry = fullWeek[dayName];
    // Merge: date data overlays day-name data
    let entry;
    if (dateEntry && dayEntry) {
      entry = { ...dayEntry, ...dateEntry, data: { ...(dayEntry.data || {}), ...(dateEntry.data || {}) } };
    } else {
      entry = dateEntry || dayEntry || null;
    }
    // Extract blocks from entry.data (A_BLOCK, B_BLOCK, etc.)
    const blockKeys = entry?.data ? Object.keys(entry.data).filter(k => k.endsWith('_BLOCK')).sort() : [];
    const blocks = blockKeys.map(key => ({ key, ...entry.data[key] }));
    const isToday = dateStr === todayStr;
    return { dayName, dateStr, blocks, isToday: dateStr === todayStr, shortDay, dayNum, monthShort, programName: entry?.name || '' };
  });

  const selectedCard = selectedDay ? dayCards.find(c => c.dateStr === selectedDay) : null;

  return (
    <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#e2e8f0", fontFamily: "'Inter','DM Sans',sans-serif" }}>
      {/* Header */}
      <header style={{ padding: "12px 16px 8px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button
          onClick={goBack}
          style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)", color: "#64748b", padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}
        >
          ← Exit
        </button>
        <div style={{ textAlign: "center" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {gymLogo && <img src={gymLogo} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: "cover" }} />}
            <span style={{ fontSize: 17, fontWeight: 700 }}>{gymName || "FusionHub"}</span>
          </div>
          <div style={{ color: "#64748b", fontSize: 11, fontWeight: 600, letterSpacing: 1.5, marginTop: 2 }}>COACH VIEW</div>
        </div>
        <button
          onClick={retry}
          style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)", color: "#64748b", padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
          title="Refresh programme"
        >
          ↺
        </button>
      </header>

      {selectedCard ? (
        /* ── DETAIL VIEW ── */
        <div style={{ padding: "12px 16px", maxWidth: 700, margin: "0 auto" }}>
          <button onClick={() => setSelectedDay(null)} style={{
            background: "rgba(255,255,255,0.06)", border: "none", color: "#94a3b8", padding: "8px 16px",
            borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600, marginBottom: 12,
          }}>← Back</button>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>{selectedCard.dayName.charAt(0) + selectedCard.dayName.slice(1).toLowerCase()}</span>
              <span style={{ color: "#64748b", fontSize: 15 }}>{formatDate(selectedCard.dateStr)}</span>
              {selectedCard.isToday && <span style={{ background: "#fcdc08", color: "#0d0d1a", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 10 }}>TODAY</span>}
            </div>
            {selectedCard.programName && <div style={{ color: "#004aad", fontSize: 14, fontWeight: 600, marginTop: 4 }}>{selectedCard.programName}</div>}
          </div>

          {selectedCard.blocks.length > 0 ? (
            selectedCard.blocks.map((block, i) => <BlockCard key={i} block={block} />)
          ) : (
            <div style={{ color: "#475569", fontSize: 15, textAlign: "center", padding: "40px 0" }}>Rest day — no workout programmed</div>
          )}
        </div>
      ) : (
        /* ── DAY SELECTOR VIEW ── */
        <div style={{ padding: "12px 16px", maxWidth: 700, margin: "0 auto" }}>
          {/* Week navigation */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <button onClick={() => setWeekOffset(w => w - 1)} style={{
              background: "rgba(255,255,255,0.06)", border: "none", color: "#94a3b8", width: 36, height: 36,
              borderRadius: 8, cursor: "pointer", fontSize: 18, fontWeight: 700,
            }}>‹</button>
            <span style={{ color: "#94a3b8", fontSize: 13, fontWeight: 600 }}>
              {weekOffset === 0 ? "This Week" : weekOffset === 1 ? "Next Week" : weekOffset === -1 ? "Last Week" : `${weekOffset > 0 ? '+' : ''}${weekOffset} weeks`}
            </span>
            <button onClick={() => setWeekOffset(w => w + 1)} style={{
              background: "rgba(255,255,255,0.06)", border: "none", color: "#94a3b8", width: 36, height: 36,
              borderRadius: 8, cursor: "pointer", fontSize: 18, fontWeight: 700,
            }}>›</button>
          </div>

          {/* Day cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {dayCards.map(card => (
              <div key={card.dateStr} onClick={() => setSelectedDay(card.dateStr)} style={{
                display: "flex", alignItems: "center", padding: "14px 16px", borderRadius: 12, cursor: "pointer",
                background: card.isToday ? "linear-gradient(135deg, rgba(0,74,173,0.15), rgba(252,220,8,0.06))" : "#12121f",
                border: card.isToday ? "1.5px solid rgba(0,74,173,0.4)" : "1px solid rgba(255,255,255,0.06)",
                transition: "all 0.15s",
              }}>
                {/* Date circle */}
                <div style={{
                  width: 48, height: 48, borderRadius: 12, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", flexShrink: 0, marginRight: 14,
                  background: card.isToday ? "#004aad" : "rgba(255,255,255,0.04)",
                }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: card.isToday ? "#fcdc08" : "#64748b", lineHeight: 1 }}>{card.shortDay}</span>
                  <span style={{ fontSize: 20, fontWeight: 800, color: card.isToday ? "#fff" : "#e2e8f0", lineHeight: 1.1 }}>{card.dayNum}</span>
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>
                    {card.dayName.charAt(0) + card.dayName.slice(1).toLowerCase()}
                    <span style={{ color: "#64748b", fontWeight: 400, fontSize: 13, marginLeft: 6 }}>{card.monthShort} {card.dayNum}</span>
                  </div>
                  <div style={{ fontSize: 13, color: card.blocks.length > 0 ? "#004aad" : "#475569", fontWeight: 500, marginTop: 2 }}>
                    {card.blocks.length > 0 ? `${card.blocks.length} block${card.blocks.length > 1 ? 's' : ''} — ${card.programName || card.blocks.map(b => b.key?.replace('_BLOCK', '')).join(', ')}` : 'Rest day'}
                  </div>
                </div>

                {/* Arrow */}
                <div style={{ color: "#475569", fontSize: 20, flexShrink: 0 }}>›</div>
              </div>
            ))}
          </div>

          {weekOffset !== 0 && (
            <button onClick={() => setWeekOffset(0)} style={{
              display: "block", margin: "16px auto 0", background: "rgba(0,74,173,0.1)", border: "1px solid rgba(0,74,173,0.2)",
              color: "#004aad", padding: "8px 20px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600,
            }}>Back to This Week</button>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    background: "#0d0d1a",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  loadingWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
  },
  spinner: {
    width: 36,
    height: 36,
    border: "3px solid rgba(255,255,255,0.1)",
    borderTopColor: "#004aad",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  errorWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    color: "#f87171",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    padding: "24px 16px 16px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  logoText: {
    color: "#f1f5f9",
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: -0.5,
  },
  gymRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginTop: 4,
  },
  main: {
    padding: "16px",
    maxWidth: 960,
    margin: "0 auto",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 12,
  },
};

// Inject keyframes for spinner
if (typeof document !== "undefined") {
  const styleEl = document.createElement("style");
  styleEl.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
    @media (min-width: 640px) {
      .coach-view-grid { grid-template-columns: repeat(2, 1fr) !important; }
    }
  `;
  document.head.appendChild(styleEl);
}
